import { makeAutoObservable, runInAction } from 'mobx';
import { authApi, AuthError, LoginRequest, RegisterRequest } from '../../../shared/api/auth-api';
import { TokenStorage } from '../../../shared/lib/token-storage';
import profileApi, { ProfileData } from 'shared/api/profile-api';
import { modalStore } from '../../modal/model/modal.store';

export interface User {
  id: string;
  email: string;
  is_active: boolean;
  is_verified: boolean;
  first_name: string;
  last_name: string;
  description: string;
  gender: string;
  phone_number: string
}

type AuthStatus = 'idle' | 'pending' | 'success' | 'error';

export class UserStore {
  user: User | null = null;
  isAuthenticated: boolean = false;
  authStatus: AuthStatus = 'idle';
  authError: string | null = null;
  isVerified: boolean = false;
  profileStatus: AuthStatus = 'idle';
  profileError: string | null = null;
  profile: ProfileData | null = null;
  loading: boolean = false;

  constructor() {
    makeAutoObservable(this);
    this.checkAuth();
  }

async checkAuth() {
 
  if (!TokenStorage.hasTokens()) {
      runInAction(() => {
          this.isAuthenticated = false;
      });
      return;
  }

  try {
      const response = await authApi.refreshToken();

      runInAction(() => {
          if (response.user_id) {
              this.user = {
                  id: response.user_id,
                  email: '',
                  is_active: response.is_active,
                  is_verified: response.is_verified,
                  first_name: '',
                  last_name: '',
                  description: '',
                  gender: '',
                  phone_number: ''
              };
              this.isAuthenticated = true;
              this.isVerified = response.is_verified;
              TokenStorage.setTokens(response.access, response.refresh);
          } else {
              console.warn('checkAuth: Token refresh successful, but user_id missing in response');
          }
      });

      if (this.isAuthenticated) {
          await this.loadUserProfile();
      }
  } catch (error) {
      console.error('checkAuth: Error during authentication:', error);
      const authError = error as AuthError;
      
      runInAction(() => {
          this.isAuthenticated = false;
          this.user = null;
      });
      
      // Only remove tokens and redirect for auth errors (401), not server errors (500)
      if (authError.status_code === 401 || authError.detail?.includes('Session expired') || authError.detail?.includes('revoked')) {
          runInAction(() => {
              TokenStorage.removeTokens();
          });
          if (typeof window !== 'undefined' && window.location.pathname !== '/login' && window.location.pathname !== '/') {
              window.location.href = '/login';
          }
      }
  }
}

async login(credentials: LoginRequest & { remember_me?: boolean }) {
  this.authStatus = 'pending';
  this.authError = null;

  try {
      // Send remember_me in the API payload
      const response = await authApi.login({
          email: credentials.email,
          password: credentials.password,
          remember_me: credentials.remember_me || false
      });

      TokenStorage.setTokens(response.access, response.refresh);

      runInAction(() => {
          this.user = {
              id: response.user_id,
              email: credentials.email,
              is_active: !!response.is_active,
              is_verified: !!response.is_verified,
              first_name: '',
              last_name: '',
              description: '',
              gender: '',
              phone_number: '',
          };
          this.isAuthenticated = true;
          this.isVerified = !!response.is_verified;
          this.authStatus = 'success';
      });

      await this.loadUserProfile();
      return true;
  } catch (error) {
      console.error('login: Login failed:', error);
      runInAction(() => {
          this.authStatus = 'error';
          const authError = error as AuthError;
          this.authError = authError.detail || 'Помилка авторизації';
      });
      return false;
  }
}

async logout() {
  try {
    await authApi.logout();
  } catch (error) {
    // Log the error for debugging, but proceed with local logout
    console.error("logout: Error during API logout:", error);
  } finally {
    // Always remove tokens and reset state, regardless of API call success
    TokenStorage.removeTokens();
    runInAction(() => {
      this.user = null;
      this.isAuthenticated = false;
      this.authStatus = 'idle';
      this.authError = null;
      this.isVerified = false;
    });
    modalStore.closeModal();
  }
}

  resetAuthErrors() {
    this.authError = null;
    this.authStatus = 'idle';
  }

  // Загрузка профиля пользователя
  async loadUserProfile() {
    if (!this.isAuthenticated) {
      return false;
    }

    this.loading = true;
    this.profileStatus = 'pending';
    this.profileError = null;

    try {
      const profileData = await profileApi.getProfile();
      runInAction(() => {
        this.user = {
          ...this.user!,
          ...profileData
        };
        this.profileStatus = 'success';
      });
      return true;
    } catch (error) {
      runInAction(() => {
        this.profileStatus = 'error';
        const profileError = error as AuthError;
        this.profileError = profileError.detail || 'Ошибка загрузки профиля';
        console.error('Failed to load user profile:', error);
        
        // Only logout and redirect for real auth errors (401), not server errors (500)
        if (profileError.status_code === 401 || profileError.detail?.includes('Session expired') || profileError.detail?.includes('revoked')) {
          this.isAuthenticated = false;
          this.user = null;
          TokenStorage.removeTokens();
          
          // Redirect to login page
          if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
      });
      return false;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
}

    // Обновление профиля пользователя
    async updateProfile(data: ProfileData) {
      this.profileStatus = 'pending';
      this.profileError = null;
  
      try {
        const updatedProfile = await profileApi.updateProfile(data);
        runInAction(() => {
          this.user = {
            ...this.user!,
            ...updatedProfile
          };
          this.profileStatus = 'success';
        });
        return true;
      } catch (error) {
        runInAction(() => {
          this.profileStatus = 'error';
          const profileError = error as AuthError;
          this.profileError = profileError.detail || 'Ошибка обновления профиля';
        });
        return false;
      }
    }

  // Реєстрація нового користувача
  async register(data: RegisterRequest) {
    this.authStatus = 'pending';
    this.authError = null;

    try {
      const response = await authApi.register(data);
      TokenStorage.setTokens(response.access, response.refresh);

      runInAction(() => {
        this.user = {
          id: response.user_id,
          email: data.email,
          is_active: response.is_active,
          is_verified: response.is_verified,
          first_name: data.first_name,
          last_name: data.last_name,
          description: '',
          gender: '',
          phone_number:''
        };
        this.isAuthenticated = true;
        this.isVerified = response.is_verified;
        this.authStatus = 'success';
      });

      return true;
    } catch (error) {
      runInAction(() => {
        this.authStatus = 'error';
        const authError = error as AuthError;

        if (authError.status_code === 409) {
          this.authError = 'Користувач з таким email вже існує';
        } else {
          this.authError = authError.detail || 'Помилка реєстрації';
        }
      });
      return false;
    }
  }

  async checkOldPassword(oldPassword: string): Promise<boolean> {
    this.authStatus = 'pending';
    this.authError = null;

    try {
      await authApi.checkOldPassword({ old_password: oldPassword });
      runInAction(() => {
        this.authStatus = 'success';
      });
      return true;
    } catch (error) {
      runInAction(() => {
        this.authStatus = 'error';
        const authError = error as AuthError;
        this.authError = authError.detail || 'Помилка при перевірці паролю';
      });
      return false;
    }
  }

  // Зміна паролю (для авторизованих користувачів)
  async changePassword(newPassword1: string, newPassword2: string): Promise<boolean> {
    this.authStatus = 'pending';
    this.authError = null;

    try {
      // Запит до API для зміни паролю
      await authApi.changePassword({
        new_password1: newPassword1,
        new_password2: newPassword2
      });

      runInAction(() => {
        this.authStatus = 'success';
      });
      return true;
    } catch (error) {
      runInAction(() => {
        this.authStatus = 'error';
        const authError = error as AuthError;
        this.authError = authError.detail || 'Помилка при зміні паролю';
      });
      return false;
    }
  }

  async requestPasswordReset(email: string): Promise<boolean> {
    this.authStatus = 'pending';
    this.authError = null;

    try {
      await authApi.requestPasswordReset(email);
      runInAction(() => {
        this.authStatus = 'success';
      });
      return true;
    } catch (error) {
      runInAction(() => {
        this.authStatus = 'error';
        const authError = error as AuthError;
        this.authError = authError.detail || 'Ошибка при запросе восстановления пароля';
      });
      return false;
    }
  }

  async resetPassword(token: string, newPassword: string, confirmPassword:string,  uidb64:string): Promise<boolean> {
    this.authStatus = 'pending';
    this.authError = null;

    try {
      await authApi.resetPassword({ token, new_password1: newPassword, new_password2:confirmPassword , uid:uidb64 });
      runInAction(() => {
        this.authStatus = 'success';
      });
      return true;
    } catch (error) {
      runInAction(() => {
        this.authStatus = 'error';
        const authError = error as AuthError;
        this.authError = authError.detail || 'Ошибка при сбросе пароля';
      });
      return false;
    }
  }
}

// Створюємо єдиний екземпляр сховища
export const userStore = new UserStore();